import { formatISO, subHours } from 'date-fns'

import { IncidentActionType } from '@/graphql/generated/schemas'
import { formatFullName } from '@/utils/formats'

import {
  STALE_THRESHOLD_HOURS,
  TEAM_STALE_UNASSIGNED_GROUP_ID,
} from '../constants/constants'
import type {
  TeamStaleGroup,
  TeamStaleIncident,
  TeamStaleUser,
} from '../types/types'

/**
 * Stable identity for a group row (React keys, expansion state, testids):
 * the attribution key, or the synthetic Unassigned id for the null bucket.
 */
export const getTeamGroupKey = (group: TeamStaleGroup): string =>
  group.userId ?? TEAM_STALE_UNASSIGNED_GROUP_ID

/**
 * The staleness cutoff: incidents whose last action is at or before this
 * timestamp are stale. Recomputed per poll tick / modal open — a fixed value
 * would drift staler than the threshold over a long session.
 */
export const getStaleCutoff = (): string =>
  formatISO(subHours(new Date(), STALE_THRESHOLD_HOURS))

/**
 * The *oldest* acknowledgment of the given type, derived from `actions`
 * client-side. The schema exposes `actionUpdatedAt` on Incident, but the
 * backend cannot resolve or sort by it (the dashboard OI feature derives from
 * `actions` the same way). Oldest, not latest: the backend stale filter
 * matches an incident when ANY operator ack is at or before the cutoff, so a
 * re-acknowledged incident stays in the list because of its oldest ack — the
 * displayed time and sort order must reflect that ack, or the card would read
 * as fresh while sitting in the stale list.
 */
export const getAcknowledgedAtByType = (
  incident: TeamStaleIncident,
  type: IncidentActionType
): string | undefined =>
  (incident.actions ?? [])
    .filter((action) => action?.type === type)
    .map((action) => action?.createdAt)
    .filter((createdAt): createdAt is string => Boolean(createdAt))
    .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())[0]

/**
 * The acknowledgment time of the attributed track: operator ack for
 * owner-attributed incidents, guard ack for guard-attributed ones, falling
 * back to the other track's ack when the attributed one is missing. The
 * owner/guard precedence mirrors the server's attribution key,
 * COALESCE(owner_id, guard_id) (ENG-32213).
 */
export const getTeamAcknowledgedAt = (
  incident: TeamStaleIncident
): string | undefined => {
  const [attributedType, fallbackType] = incident.owner
    ? [
        IncidentActionType.AcknowledgementByOperator,
        IncidentActionType.AcknowledgementByGuard,
      ]
    : [
        IncidentActionType.AcknowledgementByGuard,
        IncidentActionType.AcknowledgementByOperator,
      ]

  return (
    getAcknowledgedAtByType(incident, attributedType) ??
    getAcknowledgedAtByType(incident, fallbackType)
  )
}

/**
 * Sort key for a group's loaded incident cards: the attributed ack time as
 * epoch millis, with underivable acks pushed LAST — an unknown age must not
 * read as the oldest backlog.
 */
export const getTeamAckTimestamp = (incident: TeamStaleIncident): number => {
  const acknowledgedAt = getTeamAcknowledgedAt(incident)
  return acknowledgedAt
    ? new Date(acknowledgedAt).getTime()
    : Number.MAX_SAFE_INTEGER
}

/** Display name for a group header (generated types are null-able). */
export const getTeamUserName = (user: TeamStaleUser): string =>
  formatFullName({
    firstName: user.firstName ?? undefined,
    lastName: user.lastName ?? undefined,
  })

/** Card description line: display id, facility, and device names. */
export const buildCardDescription = (incident: TeamStaleIncident): string => {
  const deviceNames = (incident.devices ?? [])
    .map((device) => device?.name)
    .filter(Boolean)
    .join(', ')

  return [incident.displayId, incident.facility?.name, deviceNames]
    .filter(Boolean)
    .join(' · ')
}
